import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import socket from '../utils/socket';
import { SkelList, SkelBlock, SkelStats, Sk } from '../components/Skeletons';

// Casa Libre message-thread skeleton: alternating chat-bubble shimmer blocks,
// shaped like the conversation that's about to load in.
const SkelThread = () => (
  <div className="cl-skel-stagger" style={{ display: 'flex', flexDirection: 'column', gap: 16, padding: '24px 8px' }}>
    {[
      { mine: false, w: '58%', h: 44 },
      { mine: true,  w: '46%', h: 30 },
      { mine: false, w: '70%', h: 60 },
      { mine: true,  w: '52%', h: 44 },
      { mine: false, w: '40%', h: 30 },
      { mine: true,  w: '64%', h: 52 },
    ].map((b, i) => (
      <div key={i} style={{ display: 'flex', justifyContent: b.mine ? 'flex-end' : 'flex-start' }}>
        <div style={{ width: b.w, maxWidth: '75%' }}>
          <SkelBlock h={b.h} />
        </div>
      </div>
    ))}
  </div>
);

// WhatsApp-style day separator label: Today / Yesterday / weekday (last 7 days)
// / full date. Used to group messages by day across all platforms.
const dayLabel = (ts) => {
  if (!ts) return '';
  const d = new Date(ts); if (isNaN(d)) return '';
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const today = startOf(new Date());
  const that = startOf(d);
  const diff = Math.round((today - that) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff > 1 && diff < 7) return d.toLocaleDateString([], { weekday: 'long' });
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
};

// ---- Brand channel glyphs (identical to the Integrations page) ----
const WhatsAppGlyph = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.414 0 .004 5.408.001 12.045a11.815 11.815 0 001.591 5.976L0 24l6.117-1.605a11.782 11.782 0 005.925 1.586h.005c6.637 0 12.048-5.409 12.051-12.047a11.816 11.816 0 00-3.576-8.519"/></svg>
);
const InstagramGlyph = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.669-.072-4.949-.2-4.358-2.621-6.78-6.982-6.98-1.28-.058-1.688-.072-4.949-.072zM12 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>
);
const WebsiteGlyph = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg>
);
const FacebookGlyph = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 2C6.5 2 2 6.1 2 11.2c0 2.9 1.4 5.4 3.7 7.1V22l3.4-1.9c.9.3 1.9.4 2.9.4 5.5 0 10-4.1 10-9.2S17.5 2 12 2z" /><path d="m7 13 3-3 2 2 3-3" /></svg>
);
const PLATFORM_BRAND = {
  whatsapp:  { bg: '#dcfce7', color: '#16a34a', Icon: WhatsAppGlyph },
  instagram: { bg: '#fce7f3', color: '#db2777', Icon: InstagramGlyph },
  website:   { bg: '#e0e7ff', color: '#4338ca', Icon: WebsiteGlyph },
  facebook:  { bg: '#e0e7ff', color: '#1d4ed8', Icon: FacebookGlyph },
};
const PLATFORM_LABEL = { whatsapp: 'WhatsApp', instagram: 'Instagram', website: 'Website', facebook: 'Messenger' };

// Render WhatsApp-style *bold* segments as <strong>.
const formatWa = (text) => {
  if (text == null) return null;
  return String(text).split(/(\*[^*\n]+\*)/g).map((p, i) =>
    (p.length > 2 && p.startsWith('*') && p.endsWith('*'))
      ? <strong key={i}>{p.slice(1, -1)}</strong>
      : <React.Fragment key={i}>{p}</React.Fragment>
  );
};

/**
 * Left contact panel — isolated and memoized so it does NOT re-render when the
 * open conversation, its messages, the input box or the "sending" state change.
 * It only re-renders when the contact data, the search term, the active chat
 * (for highlighting) or the platform actually change.
 */
const ContactList = React.memo(function ContactList({
  platform, platformLabel, conversations, searchTerm, onSearchChange,
  activeChatId, onContactClick, brandBg, brandColor, BrandIcon,
  connections, activeConnId, onPickConn, unreadByConn,
  intentTags, intentFilter, onIntentFilter, convLoading
}) {
  const q = (searchTerm || '').toLowerCase();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [minSpend, setMinSpend] = useState('');
  const [expiryFilter, setExpiryFilter] = useState(''); // '' = all; '1'|'2'|'6'|'12' = expiring within N hours
  // Intents shown on a conversation: chat-level aggregate first, else the last message's.
  const convIntents = (conv) =>
    (conv.intents && conv.intents.length ? conv.intents : (conv.lastMessage?.intents || []));
  const minSpendVal = parseFloat(minSpend);
  const filtered = conversations.filter(conv => {
    if (!`${conv._id} ${conv.name || ''} ${conv.username || ''}`.toLowerCase().includes(q)) return false;
    if (intentFilter && !convIntents(conv).some(t => String(t).toLowerCase() === intentFilter.toLowerCase())) return false;
    if (unreadOnly && !(conv.unreadCount > 0)) return false;
    if (!isNaN(minSpendVal) && minSpend !== '' && !(conv.stats && conv.stats.avgSpent >= minSpendVal)) return false;
    if (expiryFilter) {
      const WINDOW = 24 * 3600 * 1000;
      const exp = conv.lastInboundAt ? new Date(conv.lastInboundAt).getTime() + WINDOW : 0;
      const remaining = exp - Date.now();
      if (!(remaining > 0 && remaining <= Number(expiryFilter) * 3600 * 1000)) return false;
    }
    return true;
  });
  const nameFor = (conv) => conv.name || conv.username || (platform === 'whatsapp' ? `+${conv._id}` : conv._id);
  const multi = connections && connections.length > 1;

  return (
    <div className="chat-sidebar">
      <div className="chat-sidebar-header">
        <div className="chat-sidebar-title">{platformLabel} <span className="chat-count">{conversations.length}</span></div>
        {multi && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '8px 0 2px', padding: '0 4px' }}>
            {connections.map((c, i) => {
              const on = activeConnId === c.connId;
              const unread = ((unreadByConn || {})[c.connId] || 0) + (i === 0 ? ((unreadByConn || {})[''] || 0) : 0);
              return (
                <button key={c.connId} onClick={() => onPickConn(c.connId)} title={c.label}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: on ? '#111111' : '#FFFFFF', color: on ? '#F9F4EE' : '#6B6862', border: `1px solid ${on ? '#111111' : '#E7E1D6'}`, padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: 'pointer', maxWidth: 180 }}>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 130 }}>{c.label}</span>
                  {unread > 0 && <span style={{ background: on ? 'rgba(249,244,238,0.25)' : '#F6E4E1', color: on ? '#F9F4EE' : '#B23A3A', fontSize: 10.5, fontWeight: 800, borderRadius: 999, padding: '1px 6px', minWidth: 16, textAlign: 'center', fontFamily: "'IBM Plex Mono', monospace" }}>{unread}</span>}
                </button>
              );
            })}
          </div>
        )}
        <div className="chat-search-wrapper" style={{ padding: '0 4px', boxSizing: 'border-box' }}>
          <svg style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '16px', color: '#9C978C' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
          <input
            type="text"
            className="chat-search-input"
            placeholder="Search contacts..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {intentTags && intentTags.length > 0 && (
          <div style={{ position: 'relative', margin: '10px 0 2px', padding: '0 4px' }}>
            <svg style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', width: '15px', height: '15px', color: intentFilter ? '#F9F4EE' : '#9C978C', pointerEvents: 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2a1 1 0 01-.293.707L15 12.414V19a1 1 0 01-.553.894l-4 2A1 1 0 019 21v-8.586L3.293 6.707A1 1 0 013 6V4z" /></svg>
            <select
              value={intentFilter}
              onChange={(e) => onIntentFilter(e.target.value)}
              style={{
                width: '100%', boxSizing: 'border-box', appearance: 'none', WebkitAppearance: 'none',
                padding: '9px 32px 9px 36px', borderRadius: 999, cursor: 'pointer',
                border: `1px solid ${intentFilter ? '#111111' : '#E7E1D6'}`,
                background: intentFilter ? '#111111' : '#FFFFFF',
                color: intentFilter ? '#F9F4EE' : '#3A3A37',
                fontSize: 13, fontWeight: 700, outline: 'none', textTransform: 'capitalize'
              }}
            >
              <option value="">All intents</option>
              {intentTags.map((t) => (
                <option key={t._id || t.name} value={t.name}>{t.name}</option>
              ))}
            </select>
            <svg style={{ position: 'absolute', right: '16px', top: '50%', transform: 'translateY(-50%)', width: '14px', height: '14px', color: intentFilter ? '#F9F4EE' : '#9C978C', pointerEvents: 'none' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" /></svg>
          </div>
        )}

        {/* Quick filters: unread toggle + (WhatsApp) minimum average spend */}
        <div style={{ display: 'flex', gap: 6, margin: '10px 0 2px', padding: '0 4px', alignItems: 'center' }}>
          <button type="button" onClick={() => setUnreadOnly((v) => !v)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${unreadOnly ? '#111111' : '#E7E1D6'}`, background: unreadOnly ? '#111111' : '#FFFFFF', color: unreadOnly ? '#F9F4EE' : '#3A3A37', padding: '8px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: unreadOnly ? '#F9F4EE' : '#9C978C' }} />
            Unread
          </button>
          <select value={expiryFilter} onChange={(e) => setExpiryFilter(e.target.value)}
            style={{ border: `1px solid ${expiryFilter ? '#111111' : '#E7E1D6'}`, background: expiryFilter ? '#111111' : '#FFFFFF', color: expiryFilter ? '#F9F4EE' : '#3A3A37', padding: '8px 12px', borderRadius: 999, fontSize: 12.5, fontWeight: 700, cursor: 'pointer', outline: 'none', whiteSpace: 'nowrap' }}>
            <option value="">All chats</option>
            <option value="1">Expiring &lt; 1h</option>
            <option value="2">Expiring &lt; 2h</option>
            <option value="6">Expiring &lt; 6h</option>
            <option value="12">Expiring &lt; 12h</option>
          </select>
          {platform === 'whatsapp' && (
            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
              <input type="number" min="0" value={minSpend} onChange={(e) => setMinSpend(e.target.value)} placeholder="Min avg spend"
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 26px 8px 12px', borderRadius: 999, border: `1px solid ${minSpend ? '#111111' : '#E7E1D6'}`, background: minSpend ? '#FAF7F1' : '#fff', color: '#111111', fontSize: 13, fontWeight: 600, outline: 'none', fontFamily: "'IBM Plex Mono', monospace" }} />
              {minSpend !== '' && (
                <button type="button" onClick={() => setMinSpend('')} title="Clear"
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', border: 'none', background: 'none', color: '#9C978C', cursor: 'pointer', fontSize: 16, lineHeight: 1 }}>×</button>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="chat-contact-list">
        {convLoading ? (
          <SkelList rows={7} />
        ) : filtered.map((conv) => {
          const isActive = activeChatId === conv._id;
          return (
            <div
              key={conv._id}
              className={`chat-contact-item ${isActive ? 'active' : ''}`}
              onClick={() => onContactClick(conv._id)}
            >
              <div className="chat-contact-avatar" style={{ background: brandBg, color: brandColor }}>
                <BrandIcon size={22} />
              </div>
              <div className="chat-contact-content">
                <div className="chat-contact-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div className="chat-contact-name">{nameFor(conv)}</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {conv.lastMessage.handledBy && (
                      <div style={{ fontSize: '10px', color: '#2A5B8A', fontWeight: '800', textTransform: 'uppercase', background: '#E1ECF5', padding: '2px 8px', borderRadius: '999px', border: '1px solid #E7E1D6', letterSpacing: '0.02em' }}>
                        {conv.lastMessage.handledBy}
                      </div>
                    )}
                    <div className="chat-contact-time" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                      {new Date(conv.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                </div>
                <div className="chat-contact-footer">
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="chat-contact-msg">
                      {conv.lastMessage.type === 'text' ? (
                        <>{conv.lastMessage.text.body}</>
                      ) : (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: '#6B6862' }}>
                          <span style={{ display: 'flex', alignItems: 'center' }}>
                            {conv.lastMessage.type === 'image' && <svg style={{width:'14px',height:'14px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>}
                            {conv.lastMessage.type === 'video' && <svg style={{width:'14px',height:'14px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>}
                            {(conv.lastMessage.type === 'audio' || conv.lastMessage.type === 'voice') && <svg style={{width:'14px',height:'14px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" /></svg>}
                            {conv.lastMessage.type === 'document' && <svg style={{width:'14px',height:'14px'}} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>}
                          </span>
                          <span style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.02em' }}>
                            {conv.lastMessage.type.toUpperCase()}
                          </span>
                        </span>
                      )}
                    </div>
                    {((conv.intents && conv.intents.length > 0) || (conv.lastMessage?.intents && conv.lastMessage.intents.length > 0)) && (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                        {(conv.intents && conv.intents.length > 0 ? conv.intents : conv.lastMessage.intents).map((intent, idx) => (
                          <span key={`${conv._id}-${intent}-${idx}`} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10px', padding: '2px 8px', background: '#FAF7F1', color: '#111111', borderRadius: '999px', fontWeight: '700', textTransform: 'uppercase', border: '1px solid #E7E1D6', animation: 'tag-bounce 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}>
                            <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#111111', flex: 'none' }} />
                            {intent}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {(() => {
                    if (!conv.lastInboundAt) return null;
                    const remaining = new Date(conv.lastInboundAt).getTime() + 24 * 3600 * 1000 - Date.now();
                    if (!(remaining > 0 && remaining <= 12 * 3600 * 1000)) return null;
                    const chipLabel = `${Math.floor(remaining / 3600000)}h ${Math.floor((remaining % 3600000) / 60000)}m`;
                    const urgent = remaining <= 2 * 3600 * 1000;
                    return (
                      <div title="Chat window expires soon" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: urgent ? '#F6E4E1' : '#F5EAD5', color: urgent ? '#B23A3A' : '#8A5A12', fontFamily: "'IBM Plex Mono', monospace", fontSize: 10.5, fontWeight: 700, padding: '2px 8px', borderRadius: 999, whiteSpace: 'nowrap' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
                        {chipLabel}
                      </div>
                    );
                  })()}
                  {conv.unreadCount > 0 && (
                    <div className="unread-badge">{conv.unreadCount}</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

// Static waveform shape for the voice-note player (purely decorative).
const WAVE = [0.4, 0.7, 0.5, 0.85, 0.55, 0.9, 0.45, 0.75, 0.5, 0.8, 0.6, 0.35, 0.7, 0.5, 0.65, 0.42, 0.8, 0.55, 0.48, 0.72, 0.6, 0.38, 0.52, 0.82, 0.46, 0.62, 0.4, 0.7, 0.5, 0.58];

// Clean, WhatsApp-style voice player: play/pause + clickable waveform + time.
const VoicePlayer = ({ src, mine, label }) => {
  const audioRef = useRef(null);
  const barsRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dur, setDur] = useState(0);
  const [cur, setCur] = useState(0);

  const toggle = () => { const a = audioRef.current; if (!a) return; a.paused ? a.play() : a.pause(); };
  const seek = (e) => {
    const a = audioRef.current, el = barsRef.current;
    if (!a || !el || !isFinite(a.duration)) return;
    const rect = el.getBoundingClientRect();
    a.currentTime = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)) * a.duration;
  };
  const fmt = (s) => { if (!isFinite(s) || s < 0) return '0:00'; return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`; };

  return (
    <div className={`voice-note ${mine ? 'mine' : ''}`}>
      <button type="button" className="voice-play" onClick={toggle} aria-label={playing ? 'Pause' : 'Play'}>
        {playing
          ? <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
          : <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>}
      </button>
      <div className="voice-body">
        <div className="voice-bars" ref={barsRef} onClick={seek}>
          {WAVE.map((h, i) => (
            <span key={i} className={`vb ${(i + 0.5) / WAVE.length <= progress ? 'on' : ''}`} style={{ height: `${Math.round(h * 100)}%` }} />
          ))}
        </div>
        <div className="voice-meta">
          <span className="voice-label">{label}</span>
          <span className="voice-time">{fmt((playing || cur) ? cur : (dur || 0))}</span>
        </div>
      </div>
      <audio
        ref={audioRef} src={src} preload="metadata"
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
        onTimeUpdate={(e) => { const a = e.currentTarget; setCur(a.currentTime); setProgress(a.duration ? a.currentTime / a.duration : 0); }}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); setCur(0); }}
      />
    </div>
  );
};

const Chats = () => {
  const { workspaceUuid, platform: platformParam, chatId } = useParams();
  const platform = platformParam || 'whatsapp';
  const navigate = useNavigate();

  // Platform adapter — maps each platform to its endpoints + send payload shape.
  const platformConfig = {
    whatsapp: {
      base: `/api/integrations/whatsapp`,
      sendPath: `/api/integrations/whatsapp/send`,
      buildSendBody: ({ to, text, sentBy }) => ({ to, type: 'text', data: { body: text }, workspaceId: workspaceUuid, sentBy }),
      contactLabel: (id) => `+${id}`,
      supportsTemplates: true,
      supportsMedia: true
    },
    website: {
      base: `/api/integrations/website`,
      sendPath: `/api/integrations/website/agent-send`,
      buildSendBody: ({ to, text, sentBy }) => ({ to, data: { body: text }, workspaceId: workspaceUuid, sentBy }),
      contactLabel: (id) => id,
      supportsTemplates: false,
      supportsMedia: false
    },
    instagram: {
      base: `/api/integrations/instagram`,
      sendPath: `/api/integrations/instagram/send`,
      buildSendBody: ({ to, text, sentBy }) => ({ to, text, workspaceId: workspaceUuid, sentBy }),
      contactLabel: (id) => id,
      supportsTemplates: false,
      supportsMedia: true
    },
    facebook: {
      base: `/api/integrations/facebook`,
      sendPath: `/api/integrations/facebook/send`,
      buildSendBody: ({ to, text, sentBy }) => ({ to, text, workspaceId: workspaceUuid, sentBy }),
      contactLabel: (id) => id,
      supportsTemplates: false,
      supportsMedia: true
    }
  };
  const pc = platformConfig[platform] || platformConfig.whatsapp;
  const brand = PLATFORM_BRAND[platform] || PLATFORM_BRAND.whatsapp;
  const BrandIcon = brand.Icon;
  // Connections (accounts) for this platform, for the per-connection tabs.
  const [connections, setConnections] = useState([]);
  const [activeConnId, setActiveConnId] = useState(null);
  const [unreadByConn, setUnreadByConn] = useState({}); // connId -> unread total
  // Refs so the (memoised) socket handlers always read the CURRENT connection.
  const connectionsRef = useRef([]);
  const activeConnRef = useRef(null);
  const chatIdRef = useRef(null);
  const [conversations, setConversations] = useState([]);
  const [convLoading, setConvLoading] = useState(false); // loading state for the conversation list (tab/connection switch)
  const [messages, setMessages] = useState([]);
  // Uncontrolled input (ref, not state): typing must NOT re-render this large
  // component (message list + sidebar), which caused seconds of input lag.
  const chatInputRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [intentTags, setIntentTags] = useState([]);   // workspace tags/intents for the chats filter
  const [intentFilter, setIntentFilter] = useState(''); // active intent filter ('' = all)
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recSeconds, setRecSeconds] = useState(0);
  const [preview, setPreview] = useState(null); // { file, url, kind, caption } pre-send preview
  const imgInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recChunksRef = useRef([]);
  const recStreamRef = useRef(null);
  const recTimerRef = useRef(null);
  const recCancelRef = useRef(false);
  const [now, setNow] = useState(Date.now());
  const [statusMap, setStatusMap] = useState({}); // contactId -> { queryStatus, resolvedBy, resolvedAt, closed }
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [firstUnreadId, setFirstUnreadId] = useState(null);
  const [showTemplatePicker, setShowTemplatePicker] = useState(false);
  const [availableTemplates, setAvailableTemplates] = useState([]);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);

  // Tickets States
  const [showCreateTicketModal, setShowCreateTicketModal] = useState(false);
  const [showViewTicketsModal, setShowViewTicketsModal] = useState(false);
  const [chatTickets, setChatTickets] = useState([]);
  const [ticketForm, setTicketForm] = useState({ category: '', query: '', assignedTo: '' });
  const [ticketCategories, setTicketCategories] = useState([]);
  const [workspaceUsers, setWorkspaceUsers] = useState([]);

  // Analytics drawer state
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

  // Display name for a contact: use the resolved name/username (Instagram) from
  // the conversation if present, else the platform's default label (id/phone).
  const displayName = (id) => {
    const c = conversations.find(cv => cv._id === id);
    return (c && (c.name || c.username)) || pc.contactLabel(id);
  };

  // Keep refs current so socket handlers (bound once) read live values.
  connectionsRef.current = connections;
  activeConnRef.current = activeConnId;
  chatIdRef.current = chatId;

  // Query-string fragment that scopes inbox calls to the active connection.
  // Empty (today's behaviour) when the platform has 0–1 connections.
  const connQS = () => {
    const cs = connectionsRef.current, a = activeConnRef.current;
    if (!cs || cs.length < 2 || !a) return '';
    const primary = cs[0]?.connId === a ? '&primary=1' : '';
    return `&connectionId=${encodeURIComponent(a)}${primary}`;
  };
  const connBody = () => {
    const cs = connectionsRef.current, a = activeConnRef.current;
    if (!cs || cs.length < 2 || !a) return {};
    return { connectionId: a, primary: cs[0]?.connId === a ? '1' : '0' };
  };
  // Does a message's connectionId belong to the currently-viewed tab?
  // (Single-connection platforms always match. The primary tab also owns legacy
  // messages with no connectionId.)
  const belongsToActiveConn = (msgConnId) => {
    const cs = connectionsRef.current, a = activeConnRef.current;
    if (!cs || cs.length < 2 || !a) return true;
    if (String(msgConnId) === String(a)) return true;
    const isPrimaryTab = cs[0]?.connId === a;
    return isPrimaryTab && (msgConnId == null || msgConnId === '');
  };

  // Per-connection unread totals for the tab badges (covers all connections,
  // not just the active one). Legacy (null) messages count toward the primary.
  const fetchUnread = async () => {
    if (!workspaceUuid || platform === 'website') return;
    try {
      const r = await axios.get(`${API_URL}/api/workspaces/${workspaceUuid}/chat-unread?platform=${platform}`);
      setUnreadByConn(r.data?.byConnection || {});
    } catch { /* ignore */ }
  };
  // Unread count for a given connection tab (primary also gets legacy '' bucket).
  const unreadForTab = (c, i) => (unreadByConn[c.connId] || 0) + (i === 0 ? (unreadByConn[''] || 0) : 0);

  // Load this platform's connections (for the tabs).
  useEffect(() => {
    if (!workspaceUuid || platform === 'website') { setConnections([]); setActiveConnId(null); return; }
    axios.get(`${API_URL}/api/workspaces/${workspaceUuid}/integrations`).then(r => {
      const list = r.data?.[platform] || [];
      setConnections(list);
      setActiveConnId(prev => (list.find(c => c.connId === prev) ? prev : (list[0]?.connId || null)));
    }).catch(() => { setConnections([]); setActiveConnId(null); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceUuid, platform]);

  // When the connection tab changes, refresh the inbox for that connection.
  useEffect(() => {
    if (workspaceUuid && activeConnId) fetchConversations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConnId]);

  // Load per-contact resolution/closure status for the current workspace/platform.
  useEffect(() => {
    if (!workspaceUuid) return;
    axios.get(`${API_URL}/api/chat-status?workspaceUuid=${workspaceUuid}&platform=${platform}`)
      .then(r => {
        const m = {};
        (r.data.states || []).forEach(s => { m[s.contactId] = s; });
        setStatusMap(m);
      }).catch(() => {});
  }, [workspaceUuid, platform]);

  const pickConnection = (id) => {
    if (id === activeConnId) return;
    setConvLoading(true);
    setConversations([]);
    setActiveConnId(id);
    navigate(`/workspace/${workspaceUuid}/chats/${platform}`);
  };

  const scrollToTarget = () => {
    if (firstUnreadId) {
      const tagElement = document.querySelector('.unread-tag-container');
      if (tagElement) {
        tagElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
    }
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    // Reset view when platform changes so stale state from another tab doesn't leak.
    setLoading(true);
    setConversations([]);
    setMessages([]);
    fetchConversations();
    fetchUnread();

    if (workspaceUuid) {
      socket.emit('joinWorkspace', workspaceUuid);
    }

    const onNewMessage = (msg) => {
      // Drop messages from other platforms — this tab only shows `platform`.
      if (msg.platform && msg.platform !== platform) return;
      // Refresh per-connection unread badges regardless of which tab it's for.
      fetchUnread();
      // Ignore messages for a DIFFERENT connection tab — they belong elsewhere.
      if (!belongsToActiveConn(msg.connectionId)) return;

      const openChat = chatIdRef.current;
      if (openChat && (msg.from === openChat || msg.to === openChat)) {
        setMessages(prev => {
          if (prev.some(m => m._id === msg._id)) return prev;
          return [...prev, msg];
        });
        setFirstUnreadId(null);
        setTimeout(scrollToTarget, 50);
        if (msg.from === openChat) {
          markAsRead(openChat);
        }
      }
      fetchConversations();
    };

    const onIntentsUpdated = async ({ contactPhone }) => {
      try {
        const res = await axios.get(`${API_URL}${pc.base}/conversations?workspaceUuid=${workspaceUuid}${connQS()}`);
        const updatedList = res.data;
        setConversations(prev => {
          const updatedTarget = updatedList.find(c => c._id === contactPhone);
          if (!updatedTarget) return updatedList;
          return prev.map(c => c._id === contactPhone ? updatedTarget : c);
        });
        // Also refresh messages of the currently open chat so the header's
        // intent strip reflects the latest classification in real time.
        if (chatIdRef.current && contactPhone === chatIdRef.current) {
          try {
            const mRes = await axios.get(`${API_URL}${pc.base}/messages/${encodeURIComponent(chatIdRef.current)}?skip=0&limit=30&workspaceUuid=${workspaceUuid}${connQS()}`);
            setMessages(mRes.data);
          } catch {/* ignore */}
        }
      } catch (err) {
        console.error('Failed to update intents locally');
      }
    };

    const onUpdateConversation = (update) => {
      if (update.platform && update.platform !== platform) return;
      // Only touch the visible inbox if this update is for the active connection.
      if (!belongsToActiveConn(update.lastMessage?.connectionId)) return;
      setConversations(prev => {
        const index = prev.findIndex(c => c._id === update._id);
        let newList = [...prev];
        if (index !== -1) {
          const updated = { ...newList[index] };
          updated.lastMessage = update.lastMessage;
          updated.timestamp = update.timestamp;
          // Carry a freshly-resolved display name if the event provides one.
          if (update.name) updated.name = update.name;
          if (update.username) updated.username = update.username;
          if (update.unreadCount && update._id !== chatIdRef.current) {
            updated.unreadCount = (updated.unreadCount || 0) + 1;
          }
          newList.splice(index, 1);
          newList.unshift(updated);
        } else {
          newList.unshift({
            _id: update._id,
            lastMessage: update.lastMessage,
            timestamp: update.timestamp,
            name: update.name || null,
            username: update.username || null,
            unreadCount: update._id !== chatIdRef.current ? 1 : 0
          });
        }
        return newList;
      });
    };

    // A sender's profile name was resolved after the fact — patch it onto the
    // matching conversation so the list + header swap id -> name immediately.
    const onContactUpdated = (info) => {
      if (info.platform && info.platform !== platform) return;
      setConversations(prev => prev.map(c =>
        c._id === info.contactId
          ? { ...c, name: info.name || c.name, username: info.username || c.username }
          : c
      ));
    };

    const onQueryStatus = (p) => {
      if (p.workspaceUuid !== workspaceUuid) return;
      setStatusMap(prev => ({ ...prev, [p.contactId]: { ...(prev[p.contactId] || {}), queryStatus: p.queryStatus, resolvedBy: p.resolvedBy, resolvedAt: p.resolvedAt } }));
    };

    socket.on('newMessage', onNewMessage);
    socket.on('intentsUpdated', onIntentsUpdated);
    socket.on('updateConversation', onUpdateConversation);
    socket.on('contactUpdated', onContactUpdated);
    socket.on('queryStatusChanged', onQueryStatus);

    return () => {
      socket.off('newMessage', onNewMessage);
      socket.off('intentsUpdated', onIntentsUpdated);
      socket.off('updateConversation', onUpdateConversation);
      socket.off('contactUpdated', onContactUpdated);
      socket.off('queryStatusChanged', onQueryStatus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceUuid, platform]);   // NOT chatId — opening a chat must not reload/refetch the contact list

  const fetchConversations = async () => {
    try {
      const res = await axios.get(`${API_URL}${pc.base}/conversations?workspaceUuid=${workspaceUuid}${connQS()}`);
      // The chat you're currently viewing is, by definition, read — keep its
      // badge at 0 regardless of server/socket timing (fixes the unread that
      // lingered until you opened another chat).
      const open = chatIdRef.current;
      const list = open ? res.data.map(c => (c._id === open ? { ...c, unreadCount: 0 } : c)) : res.data;
      setConversations(list);
      setLoading(false);
      setConvLoading(false);
    } catch (err) {
      setConvLoading(false);
      console.error('Error fetching conversations:', err);
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/integrations/whatsapp/templates?workspaceUuid=${workspaceUuid}`);
      setAvailableTemplates(res.data.data.filter(t => t.status === 'APPROVED'));
    } catch (err) {
      console.error('Error fetching templates:', err);
    }
  };

  useEffect(() => {
    if (workspaceUuid) fetchTemplates();
  }, [workspaceUuid]);

  // Workspace tags/intents that populate the chats filter chips.
  useEffect(() => {
    if (!workspaceUuid) return;
    axios.get(`${API_URL}/api/tags?workspaceUuid=${workspaceUuid}`)
      .then(r => setIntentTags(Array.isArray(r.data) ? r.data : []))
      .catch(() => setIntentTags([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceUuid]);

  // Clear the active intent filter when switching channel so it doesn't carry over.
  useEffect(() => { setIntentFilter(''); }, [platform]);

  const fetchMessages = async (contactPhone, skip = 0) => {
    setMessagesLoading(true);
    try {
      const res = await axios.get(`${API_URL}${pc.base}/messages/${encodeURIComponent(contactPhone)}?skip=${skip}&limit=30&workspaceUuid=${workspaceUuid}${connQS()}`);
      
      const newBatch = res.data;
      
      if (skip === 0) {
        setMessages(newBatch);
        // Identify the FIRST unread message for the tag
        const firstUnread = newBatch.find(m => !m.read && m.direction === 'incoming');
        setFirstUnreadId(firstUnread ? firstUnread._id : null);
        setTimeout(scrollToTarget, 100);
      } else {
        setMessages(prev => [...newBatch, ...prev]);
      }
      setHasMore(newBatch.length === 30);
    } catch (err) {
      console.error('Error fetching messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const markAsRead = async (contactPhone) => {
    try {
      await axios.post(`${API_URL}${pc.base}/mark-read/${encodeURIComponent(contactPhone)}`, { workspaceUuid, ...connBody() });
      setConversations(prev => prev.map(c => c._id === contactPhone ? { ...c, unreadCount: 0 } : c));
      fetchUnread();
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  useEffect(() => {
    const initChat = async () => {
      if (chatId) {
        setPage(0);
        // If this platform has multiple connections, make sure the tab matching
        // THIS contact's connection is active (so opening from a ticket /
        // notification lands on the right account's inbox).
        if (connectionsRef.current.length > 1) {
          try {
            const r = await axios.get(`${API_URL}${pc.base}/messages/${encodeURIComponent(chatId)}?skip=0&limit=1&workspaceUuid=${workspaceUuid}`);
            const cid = r.data?.[0]?.connectionId || connectionsRef.current[0]?.connId;
            if (cid && cid !== activeConnRef.current) {
              activeConnRef.current = cid; // update ref now so the fetches below scope correctly
              setActiveConnId(cid);
            }
          } catch { /* ignore */ }
        }
        await fetchMessages(chatId, 0); // Deterministic fetch
        await markAsRead(chatId); // Mark AFTER fetch
      } else {
        setMessages([]);
      }
    };
    initChat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatId]);

  const loadMore = () => {
    const nextSkip = (page + 1) * 30;
    setPage(page + 1);
    fetchMessages(chatId, nextSkip);
  };

  const handleSendMessage = async () => {
    const textToSave = (chatInputRef.current?.value || '').trim();
    if (!textToSave || !chatId) return;
    if (chatInputRef.current) chatInputRef.current.value = '';
    setSending(true);
    setFirstUnreadId(null);
    
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userName = user.name || 'Admin';

    try {
      await axios.post(`${API_URL}${pc.sendPath}`, { ...pc.buildSendBody({ to: chatId, text: textToSave, sentBy: userName }), connectionId: (connections.length > 1 ? activeConnId : undefined) });
    } catch (err) {
      console.error('Error sending message:', err);
    } finally {
      setSending(false);
    }
  };

  // Upload + send an image or voice note via the platform's /send-media route.
  // The message appears in the thread via the socket 'newMessage' broadcast.
  const sendMediaFile = async (file, kind, caption) => {
    if (!file || !chatId) return false;
    setSending(true);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const form = new FormData();
    form.append('file', file);
    form.append('to', chatId);
    form.append('workspaceId', workspaceUuid);
    form.append('sentBy', user.name || 'Admin');
    form.append('kind', kind);
    if (caption && caption.trim()) form.append('caption', caption.trim());
    if (connections.length > 1 && activeConnId) form.append('connectionId', activeConnId);
    try {
      await axios.post(`${API_URL}${pc.base}/send-media`, form, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      return true;
    } catch (err) {
      alert('Could not send: ' + (err.response?.data?.error || err.message));
      return false;
    } finally {
      setSending(false);
    }
  };

  // Pick an image/video → open a preview so the user can confirm before sending.
  const onPickMedia = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    const kind = file.type.startsWith('video') ? 'video' : 'image';
    setPreview({ file, url: URL.createObjectURL(file), kind, caption: '' });
  };

  const clearPreview = () => {
    setPreview((p) => { if (p?.url) URL.revokeObjectURL(p.url); return null; });
  };

  const confirmSendPreview = async () => {
    if (!preview) return;
    const ok = await sendMediaFile(preview.file, preview.kind, preview.caption);
    if (ok) clearPreview();
  };

  const startRecording = async () => {
    if (recording) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
      const mimeType = types.find((t) => window.MediaRecorder && MediaRecorder.isTypeSupported(t)) || '';
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recChunksRef.current = [];
      recCancelRef.current = false;
      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size) recChunksRef.current.push(ev.data); };
      mr.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        clearInterval(recTimerRef.current);
        setRecording(false);
        setRecSeconds(0);
        if (recCancelRef.current) return;
        const type = mr.mimeType || 'audio/webm';
        const ext = type.includes('mp4') ? 'm4a' : type.includes('ogg') ? 'ogg' : 'webm';
        const blob = new Blob(recChunksRef.current, { type });
        if (blob.size > 0) sendMediaFile(new File([blob], `voice.${ext}`, { type }), 'voice');
      };
      mediaRecorderRef.current = mr;
      recStreamRef.current = stream;
      mr.start();
      setRecording(true);
      setRecSeconds(0);
      recTimerRef.current = setInterval(() => setRecSeconds((s) => s + 1), 1000);
    } catch (err) {
      alert('Microphone access is required to record a voice message.');
    }
  };

  const stopRecording = (send) => {
    recCancelRef.current = !send;
    try { mediaRecorderRef.current?.stop(); } catch { /* noop */ }
  };

  const fmtRec = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const handleSendTemplate = async (template) => {
    setSending(true);
    setShowTemplatePicker(false);
    
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userName = user.name || 'Admin';

    try {
      await axios.post(`${API_URL}/api/integrations/whatsapp/send`, {
        to: chatId,
        type: 'template',
        data: {
          name: template.name,
          language: { code: template.language }
        },
        workspaceId: workspaceUuid,
        sentBy: userName
      });
    } catch (err) {
      alert('Error sending template: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const handleSendOrderConfirmation = async () => {
    setSending(true);
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userName = user.name || 'Admin';

    try {
      await axios.post(`${API_URL}/api/integrations/whatsapp/send`, {
        to: chatId,
        type: 'interactive',
        data: {
          type: 'button',
          header: { type: 'text', text: 'Order Confirmation' },
          body: { text: 'Your order #12345 has been prepared. Please confirm if you wish to proceed.' },
          footer: { text: 'Powered by Vetobase' },
          action: {
            buttons: [
              { type: 'reply', reply: { id: 'confirm_order', title: 'Confirm' } },
              { type: 'reply', reply: { id: 'reject_order', title: 'Reject' } }
            ]
          }
        },
        workspaceId: workspaceUuid,
        sentBy: userName
      });
    } catch (err) {
      alert('Error sending buttons: ' + err.message);
    } finally {
      setSending(false);
    }
  };

  const loadTicketCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tickets/categories?workspaceUuid=${workspaceUuid}`);
      setTicketCategories(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadWorkspaceUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/users/workspace-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWorkspaceUsers(res.data || []);
    } catch (err) {
      console.error('Failed to load users for assignment:', err);
    }
  };

  const handleOpenCreateTicket = () => {
    loadTicketCategories();
    loadWorkspaceUsers();
    setTicketForm({ category: '', query: '', assignedTo: '' });
    setShowCreateTicketModal(true);
  };

  const handleCreateTicketSubmit = async () => {
    if (!ticketForm.category || !ticketForm.query) return alert('Category and Query required');
    const userObj = JSON.parse(localStorage.getItem('user') || '{}');
    try {
      await axios.post(`${API_URL}/api/tickets`, {
        workspaceUuid,
        platform,
        associatedPhone: chatId,
        category: ticketForm.category,
        query: ticketForm.query,
        assignedTo: ticketForm.assignedTo,
        createdBy: userObj.name || 'Agent'
      });
      setShowCreateTicketModal(false);
    } catch (err) {
      alert('Error saving ticket: ' + err.message);
    }
  };

  const handleViewTickets = async () => {
    setShowViewTicketsModal(true);
    try {
      const res = await axios.get(`${API_URL}/api/tickets?workspaceUuid=${workspaceUuid}&associatedPhone=${chatId}`);
      setChatTickets(res.data.tickets || []);
    } catch (err) {
      console.error(err);
    }
  };

  const toggleModalOutside = (e, setter) => {
    if (e.target === e.currentTarget) {
      setter(false);
    }
  };

  const handleContactClick = useCallback((id) => {
    navigate(`/workspace/${workspaceUuid}/chats/${platform}/${encodeURIComponent(id)}`);
  }, [navigate, workspaceUuid, platform]);

  const handleOpenAnalytics = async () => {
    if (!chatId) return;
    setShowAnalytics(true);
    setAnalyticsLoading(true);
    setAnalytics(null);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API_URL}/api/analytics/chat-intents?workspaceUuid=${workspaceUuid}&platform=${platform}&contactId=${encodeURIComponent(chatId)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAnalytics(res.data);
    } catch (err) {
      console.error('Analytics fetch failed:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const renderMedia = (msg) => {
    const { media } = msg;
    if (!media) return null;

    // Instagram/Messenger send a direct URL; WhatsApp sends an id fetched via the proxy.
    // The proxy is auth-protected, but <img>/<video>/<audio> tags can't send the
    // Authorization header — so pass the JWT in the query string instead.
    const authToken = localStorage.getItem('token');
    const mediaUrl = media.url
      ? media.url
      : (media.id ? `${API_URL}/api/integrations/whatsapp/media/${media.id}?workspaceUuid=${workspaceUuid}&token=${authToken}` : null);

    if (msg.type === 'share') {
      return (
        <div className="media-container">
          <a href={mediaUrl || '#'} target="_blank" rel="noreferrer" className="document-link">
            <div className="document-box">
              <span className="document-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg></span>
              <div className="document-info">
                <div className="document-name">{(msg.text && msg.text.body) || 'Shared post / reel'}</div>
                <div className="document-action">{mediaUrl ? 'OPEN' : 'Shared content'}</div>
              </div>
            </div>
          </a>
        </div>
      );
    }

    if (!mediaUrl) return null;

    if (msg.type === 'image') {
      return (
        <div className="media-container image">
          <img src={mediaUrl} alt="WhatsApp Image" className="chat-media-img" loading="lazy" />
          {media.caption && <div className="media-caption">{media.caption}</div>}
        </div>
      );
    }

    if (msg.type === 'video') {
      return (
        <div className="media-container video">
          <video src={mediaUrl} controls className="chat-media-video" />
          {media.caption && <div className="media-caption">{media.caption}</div>}
        </div>
      );
    }

    if (msg.type === 'audio' || msg.type === 'voice') {
      return (
        <VoicePlayer
          src={mediaUrl}
          mine={msg.direction === 'outgoing'}
          label={msg.type === 'voice' ? 'Voice message' : 'Audio'}
        />
      );
    }

    if (msg.type === 'document') {
      return (
        <div className="media-container document">
          <a href={mediaUrl} target="_blank" rel="noreferrer" className="document-link">
            <div className="document-box">
              <span className="document-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg></span>
              <div className="document-info">
                <div className="document-name">{media.filename || 'Document'}</div>
                <div className="document-action">CLICK TO DOWNLOAD</div>
              </div>
            </div>
          </a>
        </div>
      );
    }
  };

  // ----- 24-hour messaging window (WhatsApp / Instagram / Messenger) -----
  // Meta only allows free-form replies within 24h of the customer's LAST inbound
  // message; each new inbound message resets the window. Outside it, the input is
  // replaced with a policy notice and reopens automatically when they message.
  const WINDOW_MS = 24 * 60 * 60 * 1000;
  const WINDOW_PLATFORMS = ['whatsapp', 'instagram', 'facebook'];
  const restricted = WINDOW_PLATFORMS.includes(platform);
  const lastInboundAt = (() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].direction === 'incoming') return new Date(messages[i].timestamp).getTime();
    }
    return 0;
  })();
  const windowExpiresAt = lastInboundAt ? lastInboundAt + WINDOW_MS : 0;
  const windowOpen = !restricted || (windowExpiresAt > 0 && now < windowExpiresAt);

  // Flip to "closed" exactly when the window expires (re-opens via the socket
  // newMessage handler when a fresh inbound message arrives → lastInboundAt moves).
  useEffect(() => {
    if (!restricted || !windowExpiresAt) return;
    const ms = windowExpiresAt - Date.now();
    if (ms <= 0) { setNow(Date.now()); return; }
    const t = setTimeout(() => setNow(Date.now()), ms + 500);
    return () => clearTimeout(t);
  }, [restricted, windowExpiresAt]);

  // Tick `now` every 30s so the "Chat expires in" countdown stays live.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);

  if (loading) {
    return (
      <div className="chat-outer-box">
        <div className="chat-sidebar">
          <div className="chat-sidebar-header">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: '4px' }}>
              <Sk w="45%" h={16} />
              <Sk w="100%" h={38} r={999} />
            </div>
          </div>
          <div className="chat-contact-list">
            <SkelList rows={8} />
          </div>
        </div>
        <div className="chat-main-view">
          <SkelThread />
        </div>
      </div>
    );
  }

  return (
    <div className={`chat-outer-box ${chatId ? 'chat-has-active' : ''}`}>
      <ContactList
        platform={platform}
        platformLabel={PLATFORM_LABEL[platform] || 'Messages'}
        conversations={conversations}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        activeChatId={chatId}
        onContactClick={handleContactClick}
        brandBg={brand.bg}
        brandColor={brand.color}
        BrandIcon={BrandIcon}
        connections={connections}
        activeConnId={activeConnId}
        onPickConn={pickConnection}
        convLoading={convLoading}
        unreadByConn={unreadByConn}
        intentTags={intentTags}
        intentFilter={intentFilter}
        onIntentFilter={setIntentFilter}
      />

      <div className="chat-main-view">
        {!chatId ? (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px', textAlign: 'center', background: 'white' }}>
            <div style={{ width: '64px', height: '64px', background: '#FAF7F1', border: '1px solid #E7E1D6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', color: '#9C978C' }}>
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
              </svg>
            </div>
            <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#111111', margin: '0 0 6px', letterSpacing: '-0.02em' }}>Select a conversation</h2>
            <p style={{ fontSize: '14px', color: '#6B6862', margin: 0, maxWidth: '360px' }}>Pick a chat from the list to view the thread and reply.</p>
          </div>
        ) : (
          <>
            <div className="chat-header">
              <div className="chat-current-user">
                <button
                  className="chat-back-mobile"
                  onClick={() => navigate(`/workspace/${workspaceUuid}/chats/${platform}`)}
                  aria-label="Back to conversations"
                >
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                </button>
                <div className="chat-contact-avatar" style={{ width: '42px', height: '42px', background: brand.bg, color: brand.color }}>
                  <BrandIcon size={20} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="chat-contact-name" style={{ marginBottom: '6px' }}>{displayName(chatId)}</div>
                  {(() => {
                    // Intents live on the chat, derived from the latest incoming message.
                    // Source of truth: the conversation's chat-level `intents` (backend aggregates
                    // from the most recent incoming message for this contact). Fall back to
                    // scanning the loaded message list if the conversation entry isn't present.
                    const convMatch = conversations.find(c => c._id === chatId);
                    const latestIncoming = [...messages].reverse().find(m => Array.isArray(m.intents) && m.intents.length > 0 && m.direction === 'incoming');
                    const intents = (convMatch?.intents && convMatch.intents.length > 0)
                      ? convMatch.intents
                      : (latestIncoming?.intents || []);
                    return (
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ fontSize: '10px', color: '#9C978C', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Intents:</span>
                        {intents.length === 0 ? (
                          <span style={{ fontSize: '11px', color: '#9C978C', fontStyle: 'italic' }}>none yet</span>
                        ) : (
                          intents.map((intent, idx) => (
                            <span key={`${chatId}-${intent}-${idx}`} style={{
                              display: 'inline-flex', alignItems: 'center', gap: '5px',
                              fontSize: '10px',
                              padding: '2px 8px',
                              background: '#FAF7F1',
                              color: '#111111',
                              borderRadius: '999px',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                              border: '1px solid #E7E1D6',
                              animation: 'tag-bounce 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)'
                            }}><span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#111111', flex: 'none' }} />{intent}</span>
                          ))
                        )}
                      </div>
                    );
                  })()}
                  {platform === 'whatsapp' && (() => {
                    const s = conversations.find(c => c._id === chatId)?.stats;
                    if (!s || !s.orders) return null;
                    const money = (v) => `${s.currency ? s.currency + ' ' : ''}${Math.round(v || 0).toLocaleString()}`;
                    return (
                      <div className="chat-cust-stats">
                        <span title="Total orders this customer placed"><b>{s.orders}</b> order{s.orders === 1 ? '' : 's'}</span>
                        <span className="ccs-dot" />
                        <span title="Average order value"><b>{money(s.avgSpent)}</b> avg</span>
                        <span className="ccs-dot" />
                        <span className={s.refunds > 0 ? 'ccs-refund' : ''} title="Number of refunded orders"><b>{s.refunds}</b> refund{s.refunds === 1 ? '' : 's'}</span>
                      </div>
                    );
                  })()}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button className="chat-head-btn" onClick={async () => {
                  const user = JSON.parse(localStorage.getItem('user') || '{}');
                  await axios.post(`${API_URL}/api/chat-status/resolve`, {
                    workspaceUuid, platform, contactId: chatId,
                    resolvedBy: user.email, resolvedByName: user.name,
                  });
                  setStatusMap(prev => ({ ...prev, [chatId]: { ...(prev[chatId] || {}), resolvedBy: user.email, resolvedAt: new Date().toISOString() } }));
                }}>Query Resolved</button>
                <button
                  onClick={handleOpenCreateTicket}
                  className="chat-head-btn"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M15 5h2a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4V7a2 2 0 0 1 2-2h2" />
                    <path d="M11 5v14" strokeDasharray="2 3" />
                  </svg>
                  Create Ticket
                </button>
                <button
                  onClick={handleViewTickets}
                  className="chat-head-btn"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
                    <line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" />
                  </svg>
                  See Tickets
                </button>
                <button
                  onClick={handleOpenAnalytics}
                  title="Chat analytics"
                  className="chat-head-btn"
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="20" x2="18" y2="10" /><line x1="12" y1="20" x2="12" y2="4" /><line x1="6" y1="20" x2="6" y2="14" />
                  </svg>
                  Analytics
                </button>
              </div>
            </div>

            {(() => {
              const st = statusMap[chatId] || {};
              const label = st.closed ? 'Chat Closed' : st.queryStatus === 'resolved' ? 'Resolved'
                : st.queryStatus === 'unresolved' ? 'User said unresolved' : 'Open';
              const bg = st.closed ? '#FAF7F1' : st.queryStatus === 'resolved' ? '#E4F1E9'
                : st.queryStatus === 'unresolved' ? '#F5EAD5' : '#E1ECF5';
              const fg = st.closed ? '#6B6862' : st.queryStatus === 'resolved' ? '#0F6E56'
                : st.queryStatus === 'unresolved' ? '#8A5A12' : '#2A5B8A';
              return (
                <div style={{ padding: '6px 16px', background: bg, color: fg, fontSize: 12.5, fontWeight: 700, display: 'flex', gap: 12, alignItems: 'center' }}>
                  <span>Status: {label}</span>
                  {st.resolvedBy && <span style={{ fontWeight: 500 }}>· marked by {st.resolvedBy}</span>}
                  {restricted && windowExpiresAt > 0 && (() => {
                    const ms = windowExpiresAt - now;
                    const urg = ms < 2 * 3600000;
                    const expLabel = ms <= 0 ? 'Expired' : `${Math.floor(ms / 3600000)}h ${Math.floor((ms % 3600000) / 60000)}m`;
                    return <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, background: urg ? '#F6E4E1' : '#FFFFFF', color: urg ? '#B23A3A' : '#6B6862', border: `1px solid ${urg ? '#F1D9D5' : '#E7E1D6'}`, padding: '4px 11px', borderRadius: 999, fontSize: 11.5, fontWeight: 700 }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
                      Expires in <span style={{ fontFamily: "'IBM Plex Mono', monospace" }}>{expLabel}</span></span>;
                  })()}
                </div>
              );
            })()}

            <div className="chat-messages-container" ref={chatContainerRef}>
              {hasMore && (
                <div style={{ textAlign: 'center', padding: '10px' }}>
                  <button className="load-more-btn" onClick={loadMore}>See More Messages</button>
                </div>
              )}
              
              {messagesLoading && page === 0 ? (
                <SkelThread />
              ) : (
                <>
                  {messages.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#9C978C', fontSize: '14px' }}>
                      No messages yet. Send a text to start.
                    </div>
                  )}
                  {messages.map((msg, index) => {
                    const isMe = msg.direction === 'outgoing';
                    const showUnreadTag = firstUnreadId && msg._id === firstUnreadId;

                    // Day separator: show before the first message of each new day.
                    const thisDay = dayLabel(msg.timestamp);
                    const prevDay = index > 0 ? dayLabel(messages[index - 1].timestamp) : null;
                    const showDay = thisDay && thisDay !== prevDay;
                    
                    // Only calculate unreadCount if we are showing the tag
                    let unreadMsgCount = 0;
                    if (showUnreadTag) {
                      unreadMsgCount = messages.slice(index).filter(m => !m.read && m.direction === 'incoming').length;
                    }
                    
                    return (
                      <React.Fragment key={msg._id || index}>
                        {showDay && (
                          <div className="chat-date-divider"><span>{thisDay}</span></div>
                        )}
                        {(showUnreadTag && unreadMsgCount > 0) && (
                          <div className="unread-tag-container">
                            <span className="unread-tag">{unreadMsgCount} UNREAD {unreadMsgCount === 1 ? 'MESSAGE' : 'MESSAGES'}</span>
                          </div>
                        )}
                        <div className={`msg-wrapper ${isMe ? 'sent' : 'received'}`}>
                          <div className={`chat-bubble ${isMe ? 'sent' : 'received'}`}>
                            {msg.type === 'text' && <div style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5, wordBreak: 'break-word' }}>{formatWa(msg.text.body)}</div>}
                            {msg.type === 'button' && (
                              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontWeight: 700, color: '#111111' }}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                {msg.text?.body}
                              </div>
                            )}
                            {msg.type === 'template' && (() => {
                              const tpl = availableTemplates.find(t => t.name === msg.template?.name);
                              const comp = (type) => tpl?.components?.find(c => c.type === type);
                              const header = comp('HEADER');
                              const body = (msg.text && msg.text.body) || comp('BODY')?.text || '';
                              const footer = comp('FOOTER');
                              const btns = comp('BUTTONS')?.buttons || [];
                              const btnIcon = (t) => {
                                if (t === 'URL') return <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>;
                                if (t === 'PHONE_NUMBER') return <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z" /></svg>;
                                return <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 17 4 12 9 7" /><path d="M20 18v-2a4 4 0 0 0-4-4H4" /></svg>;
                              };
                              return (
                                <div className="wa-tpl">
                                  <div className="wa-tpl-tag">
                                    <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><line x1="3" y1="9" x2="21" y2="9" /><line x1="9" y1="21" x2="9" y2="9" /></svg>
                                    <span>Template</span>
                                  </div>
                                  {(() => {
                                // Prefer the stored filled header; else the template's static header
                                // (but never show a raw {{n}} placeholder).
                                const hdr = msg.template?.header || (header?.format === 'TEXT' && header.text && !/\{\{\d+\}\}/.test(header.text) ? header.text : '');
                                return hdr ? <div className="wa-tpl-header">{formatWa(hdr)}</div> : null;
                              })()}
                                  {body && <div className="wa-tpl-body">{formatWa(body)}</div>}
                                  {footer?.text && <div className="wa-tpl-footer">{footer.text}</div>}
                                  {btns.length > 0 && (
                                    <div className="wa-tpl-btns">
                                      {btns.map((b, i) => (
                                        <div key={i} className="wa-tpl-btn">{btnIcon(b.type)}<span>{b.text}</span></div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                            {msg.type === 'interactive' && (
                              <div className="interactive-msg">
                                {msg.interactive?.header && (
                                  <div style={{ fontWeight: '800', marginBottom: '6px' }}>{msg.interactive.header.text}</div>
                                )}
                                <div style={{ marginBottom: (msg.interactive?.action?.buttons?.length || msg.interactive?.type === 'button_reply') ? '10px' : 0 }}>{msg.interactive?.body?.text || msg.text?.body}</div>

                                {msg.interactive?.type === 'button' && msg.interactive.action?.buttons && (
                                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', borderTop: '1px solid currentColor', paddingTop: '8px', opacity: 0.8 }}>
                                    {msg.interactive.action.buttons.map((btn, i) => (
                                      <div key={i} style={{ textAlign: 'center', fontSize: '13px', padding: '6px', borderRadius: '4px', background: 'rgba(0,0,0,0.05)' }}>
                                        {btn.reply?.title}
                                      </div>
                                    ))}
                                  </div>
                                )}

                                {msg.interactive?.type === 'button_reply' && (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: '#FAF7F1', borderRadius: '8px', color: '#111111', fontWeight: '700' }}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
                                    <span>Selected: {msg.interactive.button_reply.title}</span>
                                  </div>
                                )}
                              </div>
                            )}
                            {['image', 'video', 'audio', 'voice', 'document', 'share'].includes(msg.type) && renderMedia(msg)}
                            {isMe && msg.sentBy && (
                              <div style={{ fontSize: '9px', opacity: 0.7, marginTop: '4px', textAlign: 'right', fontStyle: 'italic', fontWeight: '600' }}>
                                sent by: {msg.sentBy}
                              </div>
                            )}
                          </div>
                          <div className="msg-timestamp" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {preview && (
              <div className="media-preview-overlay" onClick={(e) => { if (e.target === e.currentTarget) clearPreview(); }}>
                <div className="media-preview-box">
                  <button type="button" className="mp-close" onClick={clearPreview} aria-label="Close">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.4"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                  </button>
                  <div className="mp-stage">
                    {preview.kind === 'video'
                      ? <video src={preview.url} controls autoPlay className="mp-media" />
                      : <img src={preview.url} alt="preview" className="mp-media" />}
                  </div>
                  <div className="mp-bar">
                    <input
                      className="mp-caption"
                      placeholder="Add a caption…"
                      value={preview.caption}
                      onChange={(e) => setPreview((p) => ({ ...p, caption: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') confirmSendPreview(); }}
                      autoFocus
                    />
                    <button type="button" className="mp-send" disabled={sending} onClick={confirmSendPreview} title="Send">
                      {sending
                        ? <div className="three-dots-loader" style={{ height: 18 }}><div className="dot1" style={{ width: 4, height: 4, background: 'white' }} /><div className="dot2" style={{ width: 4, height: 4, background: 'white' }} /><div className="dot3" style={{ width: 4, height: 4, background: 'white' }} /></div>
                        : <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>}
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="chat-input-bar">
              {showTemplatePicker && (
                <div style={{ position: 'absolute', bottom: '100%', left: '24px', right: '24px', background: 'white', border: '1px solid #E7E1D6', borderBottom: 'none', borderRadius: '16px 16px 0 0', boxShadow: '4px 4px 0 rgba(17,17,17,.2)', zIndex: 100, padding: '20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                    <h4 style={{ margin: 0 }}>Approved Templates</h4>
                    <button onClick={() => setShowTemplatePicker(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>&times;</button>
                  </div>
                  <div style={{ maxHeight: '200px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {availableTemplates.map(t => (
                      <button 
                        key={t.id} 
                        onClick={() => handleSendTemplate(t)}
                        style={{ textAlign: 'left', padding: '12px', borderRadius: '12px', border: '1px solid #E7E1D6', background: '#FAF7F1', cursor: 'pointer' }}
                      >
                        <div style={{ fontSize: '13px', fontWeight: '700' }}>{t.name}</div>
                        <div style={{ fontSize: '11px', color: '#6B6862' }}>{t.category}</div>
                      </button>
                    ))}
                    {availableTemplates.length === 0 && <div style={{ fontSize: '12px', color: '#6B6862' }}>No approved templates found.</div>}
                  </div>
                </div>
              )}
              
              {windowOpen ? (
                <form className="chat-input-form" style={{ position: 'relative' }} onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}>
                  {recording ? (
                    <div className="chat-recording">
                      <span className="rec-dot" />
                      <span className="rec-time">{fmtRec(recSeconds)}</span>
                      <span className="rec-label">Recording voice message…</span>
                      <button type="button" className="rec-btn rec-cancel" title="Cancel" onClick={() => stopRecording(false)}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /></svg>
                      </button>
                      <button type="button" className="rec-btn rec-send" title="Send" onClick={() => stopRecording(true)}>
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                      </button>
                    </div>
                  ) : sending ? (
                    <div className="chat-sending">
                      <span className="chat-sending-text">Sending</span>
                      <span className="chat-sending-dots"><i></i><i></i><i></i></span>
                    </div>
                  ) : (
                    <>
                      {pc.supportsMedia && (
                        <>
                          <input type="file" accept="image/*,video/*" ref={imgInputRef} style={{ display: 'none' }} onChange={onPickMedia} />
                          <button type="button" className="chat-attach-btn" title="Send photo or video" onClick={() => imgInputRef.current?.click()}>
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2" /><circle cx="8.5" cy="8.5" r="1.5" /><polyline points="21 15 16 10 5 21" /></svg>
                          </button>
                          <button type="button" className="chat-attach-btn" title="Record voice message" onClick={startRecording}>
                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" /><path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /></svg>
                          </button>
                        </>
                      )}
                      <input
                        type="text"
                        className="chat-input"
                        placeholder="Type your message here..."
                        ref={chatInputRef}
                        defaultValue=""
                      />
                    </>
                  )}
                  {!recording && (
                    <button type="submit" className="chat-action-btn" disabled={sending}>
                      {sending ? (
                        <div className="three-dots-loader" style={{ height: '20px' }}>
                          <div className="dot1" style={{ width: '4px', height: '4px', background: 'white' }}></div>
                          <div className="dot2" style={{ width: '4px', height: '4px', background: 'white' }}></div>
                          <div className="dot3" style={{ width: '4px', height: '4px', background: 'white' }}></div>
                        </div>
                      ) : (
                        <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>
                      )}
                    </button>
                  )}
                </form>
              ) : (
                <div className="chat-window-closed">
                  <div className="chat-window-closed-ic">
                    <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 15 14" /></svg>
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div className="chat-window-closed-title">The 24‑hour messaging window has closed</div>
                    <div className="chat-window-closed-sub">
                      Under {(PLATFORM_LABEL[platform] || 'the platform')}'s Meta policy, free‑form replies are only allowed within 24&nbsp;hours of the customer's last message. This conversation will reopen automatically the moment {displayName(chatId)} messages again.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
      {/* CREATE TICKET MODAL */}
      {showCreateTicketModal && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(17, 17, 17, 0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => toggleModalOutside(e, setShowCreateTicketModal)}>
          <div style={{ background: 'white', border: '1px solid #E7E1D6', borderRadius: '20px', padding: '24px', width: '450px', boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', letterSpacing: '-0.03em', color: '#111111' }}>Create Ticket</h3>
                <p style={{ margin: 0, fontSize: '12px', color: '#6B6862' }}>For {displayName(chatId)}</p>
              </div>
              <button onClick={() => setShowCreateTicketModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '24px', color: '#9C978C' }}>&times;</button>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#6B6862', textTransform: 'uppercase', marginBottom: '6px', display: 'block' }}>Category *</label>
                <input 
                  type="text"
                  list="category-options"
                  value={ticketForm.category}
                  onChange={(e) => setTicketForm({ ...ticketForm, category: e.target.value })}
                  placeholder="e.g. Refunds, Bug Report..."
                  style={{ width: '100%', padding: '12px', fontSize: '14px', borderRadius: '14px', border: '1px solid #E7E1D6', outline: 'none', boxSizing: 'border-box' }}
                />
                <datalist id="category-options">
                  {ticketCategories.map(cat => <option key={cat} value={cat} />)}
                </datalist>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#6B6862', textTransform: 'uppercase', marginBottom: '6px', display: 'block' }}>Assigned To</label>
                <select 
                  value={ticketForm.assignedTo}
                  onChange={(e) => setTicketForm({ ...ticketForm, assignedTo: e.target.value })}
                  style={{ width: '100%', padding: '12px', fontSize: '14px', borderRadius: '14px', border: '1px solid #E7E1D6', outline: 'none', boxSizing: 'border-box', background: 'white' }}
                >
                  <option value="">Select a user...</option>
                  {workspaceUsers.map(user => (
                    <option key={user._id} value={user.name || user.email}>{(user.name || user.email)} ({user.role})</option>
                  ))}
                  <option value="Unassigned">Unassigned</option>
                </select>
              </div>

              <div>
                <label style={{ fontSize: '12px', fontWeight: '700', color: '#6B6862', textTransform: 'uppercase', marginBottom: '6px', display: 'block' }}>Query / Context *</label>
                <textarea 
                  value={ticketForm.query}
                  onChange={(e) => setTicketForm({ ...ticketForm, query: e.target.value })}
                  placeholder="Describe the issue..."
                  style={{ width: '100%', padding: '12px', fontSize: '14px', borderRadius: '14px', border: '1px solid #E7E1D6', outline: 'none', minHeight: '100px', boxSizing: 'border-box' }}
                />
              </div>

              <button
                onClick={handleCreateTicketSubmit}
                style={{ background: '#111111', color: '#F9F4EE', border: 'none', padding: '12px', borderRadius: '999px', fontWeight: '700', width: '100%', cursor: 'pointer', marginTop: '8px', boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
                Save Ticket
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ANALYTICS DRAWER (right side) */}
      {showAnalytics && (
        <div
          style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '420px', maxWidth: '100vw', background: 'white', borderLeft: '1px solid #E7E1D6', boxShadow: 'none', zIndex: 200, display: 'flex', flexDirection: 'column' }}
        >
          <div style={{ padding: '18px 22px', borderBottom: '1px solid #E7E1D6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '-0.03em', color: '#111111' }}>Chat Analytics</div>
              <div style={{ fontSize: '11px', color: '#9C978C', marginTop: '2px' }}>{displayName(chatId)}</div>
            </div>
            <button onClick={() => setShowAnalytics(false)} style={{ background: 'none', border: 'none', fontSize: '22px', color: '#9C978C', cursor: 'pointer' }}>&times;</button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>
            {analyticsLoading && (
              <>
                <SkelStats count={2} />
                <SkelBlock h={180} />
              </>
            )}
            {!analyticsLoading && analytics && (
              <>
                {/* AI summary */}
                {analytics.summary ? (
                  <div style={{ background: '#E1ECF5', border: '1px solid #E7E1D6', padding: '14px 16px', borderRadius: '12px', marginBottom: '20px' }}>
                    <div style={{ fontSize: '10px', fontWeight: 800, color: '#2A5B8A', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>AI Summary</div>
                    <div style={{ fontSize: '13px', color: '#111111', lineHeight: 1.5 }}>{analytics.summary}</div>
                  </div>
                ) : (
                  <div style={{ background: '#FAF7F1', border: '1px dashed #E7E1D6', padding: '14px 16px', borderRadius: '12px', marginBottom: '20px', color: '#9C978C', fontSize: '12px' }}>
                    Not enough intent history to generate an insight yet.
                  </div>
                )}

                {/* Total */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
                  <div style={{ flex: 1, background: '#FAF7F1', border: '1px solid #E7E1D6', padding: '14px', borderRadius: '12px' }}>
                    <div style={{ fontSize: '10px', color: '#6B6862', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total detections</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, letterSpacing: '-0.03em', color: '#111111', marginTop: '4px', fontFamily: "'IBM Plex Mono', monospace" }}>{analytics.totalDetections}</div>
                  </div>
                  <div style={{ flex: 1, background: '#FAF7F1', border: '1px solid #E7E1D6', padding: '14px', borderRadius: '12px' }}>
                    <div style={{ fontSize: '10px', color: '#6B6862', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Unique intents</div>
                    <div style={{ fontSize: '24px', fontWeight: 700, letterSpacing: '-0.03em', color: '#111111', marginTop: '4px', fontFamily: "'IBM Plex Mono', monospace" }}>{Object.keys(analytics.counts || {}).length}</div>
                  </div>
                </div>

                {/* Frequency bars */}
                <div style={{ marginBottom: '24px' }}>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px' }}>Intent frequency</div>
                  {Object.keys(analytics.counts || {}).length === 0 ? (
                    <div style={{ fontSize: '12px', color: '#9C978C' }}>No intents detected yet.</div>
                  ) : (() => {
                    const max = Math.max(...Object.values(analytics.counts));
                    return Object.entries(analytics.counts).sort((a, b) => b[1] - a[1]).map(([intent, n]) => (
                      <div key={intent} style={{ marginBottom: '10px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#111111', fontWeight: 600, marginBottom: '4px' }}>
                          <span style={{ textTransform: 'uppercase', letterSpacing: '0.04em', fontSize: '11px' }}>{intent}</span>
                          <span style={{ color: '#6B6862', fontFamily: "'IBM Plex Mono', monospace" }}>{n}</span>
                        </div>
                        <div style={{ height: '6px', background: '#FAF7F1', borderRadius: '3px', overflow: 'hidden' }}>
                          <div style={{ width: `${(n / max) * 100}%`, height: '100%', background: '#111111' }} />
                        </div>
                      </div>
                    ));
                  })()}
                </div>

                {/* Timeline */}
                <div>
                  <div style={{ fontSize: '11px', fontWeight: 800, color: '#6B6862', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '10px' }}>Timeline (most recent first)</div>
                  {(analytics.timeline || []).length === 0 ? (
                    <div style={{ fontSize: '12px', color: '#9C978C' }}>Empty.</div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {[...analytics.timeline].reverse().slice(0, 50).map((t, i) => (
                        <div key={i} style={{ padding: '10px 12px', background: '#FAF7F1', border: '1px solid #E7E1D6', borderRadius: '8px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 800, color: '#111111', textTransform: 'uppercase', letterSpacing: '0.04em', background: '#FFFFFF', border: '1px solid #E7E1D6', padding: '2px 8px', borderRadius: '999px' }}>{t.intent}</span>
                            <span style={{ fontSize: '10px', color: '#9C978C', fontFamily: "'IBM Plex Mono', monospace" }}>{new Date(t.detectedAt).toLocaleString()}</span>
                          </div>
                          {t.messagePreview && (
                            <div style={{ fontSize: '12px', color: '#3A3A37', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>"{t.messagePreview}"</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* VIEW TICKETS MODAL */}
      {showViewTicketsModal && (
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(17, 17, 17, 0.3)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={(e) => toggleModalOutside(e, setShowViewTicketsModal)}>
          <div style={{ background: 'white', border: '1px solid #E7E1D6', borderRadius: '20px', padding: '24px', width: '550px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '4px 4px 0 rgba(17,17,17,.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', letterSpacing: '-0.03em', color: '#111111' }}>Tickets</h3>
                <p style={{ margin: 0, fontSize: '12px', color: '#6B6862' }}>For {displayName(chatId)}</p>
              </div>
              <button onClick={() => setShowViewTicketsModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '24px', color: '#9C978C' }}>&times;</button>
            </div>
            
            <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '12px', paddingRight: '8px' }}>
              {chatTickets.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '40px', color: '#9C978C' }}>No tickets associated with this contact yet.</div>
              ) : (
                chatTickets.map(ticket => (
                  <div key={ticket._id} style={{ border: '1px solid #E7E1D6', borderRadius: '12px', padding: '16px', background: '#FAF7F1' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ fontSize: '12px', fontWeight: '700', background: '#FAF7F1', color: '#3A3A37', padding: '4px 10px', borderRadius: '999px', border: '1px solid #E7E1D6' }}>{ticket.category}</span>
                        <span style={{ fontSize: '11px', color: '#3A3A37', background: '#FFFFFF', border: '1px solid #E7E1D6', padding: '4px 10px', borderRadius: '999px', fontWeight: '700', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: ticket.status === 'completed' ? '#0F6E56' : '#9C978C' }} />
                          {ticket.status.toUpperCase()}
                        </span>
                      </div>
                      <span style={{ fontSize: '11px', color: '#9C978C', fontWeight: '600', fontFamily: "'IBM Plex Mono', monospace" }}>{new Date(ticket.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div style={{ fontSize: '13px', color: '#3A3A37', lineHeight: '1.5', margin: '12px 0', background: 'white', padding: '10px', borderRadius: '8px', border: '1px solid #E7E1D6' }}>{ticket.query}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#6B6862', fontWeight: '600' }}>
                      <span>By: {ticket.createdBy}</span>
                      <span>Assigned to: {ticket.assignedTo || 'Unassigned'}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Chats;
